import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Instrument, MarketStatus, Tick } from '@dabba/contracts';
import { api, type PortfolioResponse } from '../api/client';
import { useSession } from '../store/session';
import { useWatchlist } from '../store/watchlist';
import { useTicks } from './DataProvider';

/** Server-computed market phase — never inferred from tick flow. */
export function useMarketStatus(): MarketStatus | null {
  const { data } = useQuery({
    queryKey: ['marketStatus'],
    queryFn: api.marketStatus,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  return data ?? null;
}

/**
 * Metadata for a list of stored symbols, in ONE request. Cached hard for the
 * session: the instrument master is re-issued once a day, so a contract's
 * strike and expiry cannot change under us.
 */
export function useContractMeta(symbols: string[]): Record<string, Instrument> {
  const key = [...new Set(symbols)].sort().join(',');
  const { data } = useQuery({
    queryKey: ['instrumentsMeta', key],
    queryFn: () => api.instrumentsMeta(key.split(',')),
    enabled: key.length > 0,
    staleTime: 6 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
  return useMemo(() => {
    const bySymbol: Record<string, Instrument> = {};
    for (const i of data?.results ?? []) bySymbol[i.symbol] = i;
    return bySymbol;
  }, [data]);
}

/**
 * Pull the account's watchlist down once per sign-in and merge it into the
 * local store.
 *
 * The merge is a UNION, not a replace: a visitor who built a list before
 * signing in would otherwise watch it vanish the moment they did. Local order
 * wins for symbols on both sides, and the merged list is pushed straight back
 * so the account ends up holding everything.
 */
export function useWatchlistSync(): void {
  const token = useSession((s) => s.token);
  const synced = useWatchlist((s) => s.synced);
  const { data } = useQuery({
    queryKey: ['watchlist'],
    queryFn: api.getWatchlist,
    enabled: !!token && !synced,
    staleTime: Infinity,
    retry: 1,
  });

  useEffect(() => {
    if (!data) return;
    const store = useWatchlist.getState();
    if (store.synced) return;
    const local = store.symbols;
    const merged = [...local, ...data.symbols.filter((s) => !local.includes(s))];
    store.hydrateFrom(merged);
    // Push only when the union actually differs from what the server holds,
    // so a plain sign-in on a fresh browser stays a read.
    const same =
      merged.length === data.symbols.length && merged.every((s, i) => s === data.symbols[i]);
    if (!same) void api.putWatchlist(merged.slice(0, 30)).catch(() => undefined);
  }, [data]);
}

/**
 * Everything that changes when an order fills. Called from the order ticket
 * and from the live fill events, so no screen is left showing a stale book.
 */
export function useInvalidateTrading(): () => void {
  const qc = useQueryClient();
  return () =>
    qc.invalidateQueries({
      predicate: (q) => {
        const key = q.queryKey[0];
        return (
          key === 'orders' ||
          key === 'positions' ||
          key === 'portfolio' ||
          key === 'holdings' ||
          key === 'wallet' ||
          key === 'journal' ||
          key === 'trades' ||
          key === 'pnlReport'
        );
      },
    });
}

/**
 * The portfolio, marked to the live feed instead of to whatever the last poll
 * happened to serialize.
 *
 * The server owns everything that only moves when a fill happens — quantity,
 * average cost, realized P&L, posted margin — and the poll below is the
 * reconciliation backstop for those. But current value and unrealized P&L move
 * on every tick, and reading them off the poll left the headline numbers
 * standing still for ten seconds at a stretch above a position table that was
 * already ticking. Recompute them here, from the same socket the rows use, so
 * one number can't disagree with the other.
 *
 * `availableFunds` is deliberately NOT marked: buying power is capital plus
 * cash plus realized minus POSTED margin, and an open position's
 * mark-to-market does not move any of the four.
 */
export function useLivePortfolio(): {
  data: PortfolioResponse | undefined;
  ticks: Record<string, Tick>;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const token = useSession((s) => s.token);
  const query = useQuery({
    queryKey: ['portfolio'],
    queryFn: api.portfolio,
    enabled: !!token,
    refetchInterval: 10_000,
  });

  const ticks = useTicks((query.data?.positions ?? []).map((p) => p.symbol));

  const data = useMemo(() => {
    const p = query.data;
    if (!p) return undefined;
    // Fall back to the row's serialized P&L for anything not ticking yet, so a
    // symbol the socket hasn't delivered can't silently count as zero.
    const unrealized = p.positions.reduce((sum, x) => {
      const t = ticks[x.symbol];
      return sum + (t ? (t.ltp - x.avgPrice) * x.qty : x.pnl);
    }, 0);
    const dayPnl = unrealized + p.realizedToday;
    return {
      ...p,
      dayPnl,
      dayPnlPct: p.invested > 0 ? dayPnl / p.invested : 0,
      current: p.invested + unrealized,
    };
  }, [query.data, ticks]);

  return {
    data,
    ticks,
    isPending: query.isPending,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
